import { useDispatch } from 'react-redux';

import { PermissionsEnum } from 'consts/permissions';
import Auth from 'modules/Auth';
import downloadSvg from 'assets/icons/download.svg';
import { downloadSetupErrorReportV2 } from 'modules/api/setup';
import { addToastNotification } from 'store/notification/reducer';
import { download } from 'utils/download';
import { cancelFunctions } from 'utils/cancelFunctions';
import Button, { Color, Size } from 'lib/Button';
import { SetupFrameInformation } from 'components/pages/InventoryManagement/Setup/Setup.types';
import { isErrorState } from 'components/pages/InventoryManagement/Setup/utils';
import { getSetupErrorReportFilename } from '../setupUtils';

export const NO_ERROR_REPORT_AVAILABLE = 'No error report available';
export const ERROR_FILE_GENERATION_FAILED =
  "Error File generation failed. Please contact Support with your uploaded file's name and the time of the error so we can resolve this.";
export const FAILED_TO_DOWNLOAD_ERROR_REPORT = 'Failed to download error report. Please try again.';

const ErrorDetailRenderer = ({ data }: { data: SetupFrameInformation }): React.ReactElement => {
  const dispatch = useDispatch();

  if (!isErrorState(data?.processingState) || !Auth.hasPermission(PermissionsEnum.SETUP_DOWNLOAD)) return <div>-</div>;

  const handleNoErrorReportAvailable = (): void => {
    dispatch(
      addToastNotification({
        description: NO_ERROR_REPORT_AVAILABLE,
        type: 'Warning',
        timeout: 10000,
      }),
    );
  };

  const handleDownloadErrorReport = async (): Promise<void> => {
    try {
      const response = await downloadSetupErrorReportV2(cancelFunctions, data.fileId);
      const errorReportFilename = getSetupErrorReportFilename(response.headers?.['content-disposition']);

      if (!response.data || !errorReportFilename) {
        handleNoErrorReportAvailable();
        return;
      }

      download(new Blob([response.data]), errorReportFilename);
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;

      if (status === 404) {
        handleNoErrorReportAvailable();
      } else if (status === 500 || status === 503) {
        dispatch(
          addToastNotification({
            description: ERROR_FILE_GENERATION_FAILED,
            type: 'Failure',
            timeout: 10000,
          }),
        );
      } else {
        dispatch(
          addToastNotification({
            description: FAILED_TO_DOWNLOAD_ERROR_REPORT,
            type: 'Failure',
            timeout: 10000,
          }),
        );
      }
    }
  };

  return (
    <Button
      color={Color.TRANSPARENT}
      onClick={handleDownloadErrorReport}
      size={Size.LARGE}
      svg={downloadSvg}
      label="Download"
    />
  );
};

export default ErrorDetailRenderer;
